// frontend/src/components/GroupInfoEditor.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "../i18n";

import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import es from "react-phone-number-input/locale/es.json";
import en from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";

export default function GroupInfoEditor({ groupInfo, onClose, onSave }) {
  const { _, lang } = useTranslation();

  const [formData, setFormData] = useState({
    group_name: "",
    owner_name: "",
    owner_phone: "",
    owner_email: "",
    whatsapp: false,
  });
  const [error, setError] = useState("");

  // Inicializa desde props
  useEffect(() => {
    if (groupInfo) {
      setFormData({
        group_name: groupInfo.group_name ?? "",
        owner_name: groupInfo.owner_name ?? "",
        owner_phone: groupInfo.owner_phone ?? "",
        owner_email: groupInfo.owner_email ?? "",
        whatsapp: Boolean(Number(groupInfo.whatsapp)),
      });
    }
  }, [groupInfo]);

  // Helpers de validación
  const hasText = (s) => (s || "").trim().length > 0;
  const phoneIsValid = useMemo(
    () => !!formData.owner_phone && isValidPhoneNumber(formData.owner_phone),
    [formData.owner_phone]
  );

  // Reglas: nombre grupo + responsable + teléfono válido
  const isFormValid = useMemo(() => {
    return hasText(formData.group_name) && hasText(formData.owner_name) && phoneIsValid;
  }, [formData.group_name, formData.owner_name, phoneIsValid]);

  // Si el teléfono deja de ser válido, forzar WhatsApp en false (opcional)
  useEffect(() => {
    if (!phoneIsValid && formData.whatsapp) {
      setFormData((p) => ({ ...p, whatsapp: false }));
    }
  }, [phoneIsValid]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError(""); // limpia errores al editar
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhoneChange = (value) => {
    setError("");
    setFormData((prev) => ({ ...prev, owner_phone: value || "" }));
  };

  const handleSubmit = useCallback(async () => {
    setError("");

    // Doble validación por seguridad
    if (!isFormValid) {
      setError(_("You must enter the group name and phone number."));
      return;
    }

    try {
      const session = JSON.parse(sessionStorage.getItem("hashSession"));
      const accessHash = session?.hash || "";

      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/group/info`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-access-hash": accessHash,
        },
        body: JSON.stringify({
          group_name: formData.group_name,
          owner_name: formData.owner_name,
          owner_phone: formData.owner_phone, // E.164
          owner_email: formData.owner_email,
          whatsapp: formData.whatsapp ? 1 : 0,
        }),
      });

      if (!res.ok) throw new Error(_("Could not save the changes."));
      const updated = await res.json();
      onSave(updated.group);
    } catch (err) {
      setError(err.message || _("Connection error"));
    }
  }, [_, isFormValid, formData, onSave]);

  return (
    <div className="modal-overlay">
      <div className="modal-box">
        <h2 className="modal-title">{_("Edit group information")}</h2>

        <div className="form-grid">
          <label className="form-label">
            <span className="label-highlight">{_("Group Name")}</span>
            <input
              type="text"
              name="group_name"
              value={formData.group_name}
              onChange={handleChange}
              className="input-inline"
              aria-invalid={!hasText(formData.group_name)}
            />
            {!hasText(formData.group_name) && (
              <p className="form-hint text-red-600 dark:text-red-400">
                {_("This field is required.")}
              </p>
            )}
          </label>

          <label className="form-label">
            <span className="label-highlight">{_("Responsible Name")}</span>
            <input
              type="text"
              name="owner_name"
              value={formData.owner_name}
              onChange={handleChange}
              className="input-inline"
              aria-invalid={!hasText(formData.owner_name)}
            />
            {!hasText(formData.owner_name) && (
              <p className="form-hint text-red-600 dark:text-red-400">
                {_("This field is required.")}
              </p>
            )}
          </label>

          <label className="form-label">
            <span className="label-highlight">{_("Responsible Phone")}</span>
            <PhoneInput
              placeholder={_("Enter phone number")}
              defaultCountry="DO"
              value={formData.owner_phone}
              onChange={handlePhoneChange}
              className="phone-input-custom"
              countrySelectProps={{ className: "country-select-dark-colors" }}
              labels={lang === "es" ? es : en}
            />
            {formData.owner_phone && !phoneIsValid && (
              <p className="form-hint text-red-600 dark:text-red-400">
                {_("Please enter a valid phone number.")}
              </p>
            )}
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              name="whatsapp"
              checked={formData.whatsapp}
              onChange={handleChange}
              disabled={!phoneIsValid}
            />
            {_("Contactable via WhatsApp")}
          </label>

          {error && (
            <p className="form-error" role="alert" aria-live="assertive">
              {error}
            </p>
          )}
        </div>

        <div className="form-actions">
          <button onClick={onClose} className="button-secondary">
            {_("Cancel")}
          </button>
          <button
            onClick={handleSubmit}
            className={`button-primary ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!isFormValid}
            aria-disabled={!isFormValid}
            title={!isFormValid ? _("Complete the required fields to continue.") : undefined}
          >
            {_("Save")}
          </button>
        </div>
      </div>
    </div>
  );
}

GroupInfoEditor.propTypes = {
  groupInfo: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};
